<template>
  <BaseModal :modalTitle="$t('W: Vælg analyser')">
    <div class="flex max-h-[60vh] flex-col gap-6 overflow-y-auto px-6 pb-2">
      <div
        v-for="group in analysisGroups"
        :key="group.id">
        <p class="mb-2 text-sm font-medium text-text-secondary">
          {{ group.name }}
        </p>
        <UiInputCheckboxGroupVee
          :name="`analysisIDs-${group.id}`"
          :options="group.options"
          :modelValue="checkedIDs"
          @update:model-value="onCheckedUpdate" />
      </div>

      <p
        v-if="analysisGroups.length === 0"
        class="text-sm text-text-muted">
        {{ $t('area.pricing-segment.message.no-compatible-analyses') }}
      </p>
    </div>

    <template #buttons>
      <UiBaseButton
        :text="$t('action.cancel')"
        theme="neutral"
        @button-click="closeTop" />
      <UiBaseButton
        :text="$t('action.save')"
        @button-click="onConfirmClick" />
    </template>
  </BaseModal>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { useAnalysisStore } from '@/stores/analysisStore'
import { useSampleAreaStore } from '@/stores/sampleAreaStore'
import { useAppStore } from '@/stores/appStore'
import { useModalStore } from '@/stores/modalStore'
import { mapActions, mapState } from 'pinia'
import {
  groupAnalysesBySampleArea,
  type IAnalysisPickerGroup,
} from '@/globals/typescript/utils/analysisPickerGroups'

/**
 * Mirrors PricingSegmentAnalysisPickerModal, with one deliberate difference:
 * analyses the segment already includes render checked but LOCKED, with an
 * "I segment" description under the name. This modal can only add or remove
 * customer-only analyses — disabling a segment-inherited analysis is always
 * done via the row's own toggle, so there is exactly one mechanism per
 * action rather than two paths to the same state.
 */
export default {
  name: 'CustomerAnalysisPickerModal',

  props: {
    /** Analyses the segment includes — always checked, never togglable here. */
    segmentAnalysisIDs: {
      type: Array as PropType<string[]>,
      required: true,
    },
    /** Customer-only analyses currently added — the part this modal edits. */
    customerOnlyAnalysisIDs: {
      type: Array as PropType<string[]>,
      required: true,
    },
    onConfirm: {
      type: Function as PropType<(analysisIDs: string[]) => void>,
      required: true,
    },
  },

  data() {
    return {
      localCustomerOnlyIDs: [...this.customerOnlyAnalysisIDs] as string[],
    }
  },

  computed: {
    ...mapState(useAnalysisStore, ['activeAnalyses']),
    ...mapState(useSampleAreaStore, ['allSampleAreas']),
    ...mapState(useAppStore, ['currentLocale']),

    segmentIDSet(): Set<string> {
      return new Set(this.segmentAnalysisIDs)
    },

    /**
     * Segment analyses are always in the checkbox group's modelValue so they
     * render checked; UiInputCheckboxGroupVee ignores clicks on a disabled
     * option, so they cannot be unchecked.
     */
    checkedIDs(): string[] {
      return [...this.segmentAnalysisIDs, ...this.localCustomerOnlyIDs]
    },

    analysisGroups(): IAnalysisPickerGroup[] {
      return groupAnalysesBySampleArea({
        analyses: this.activeAnalyses,
        sampleAreas: this.allSampleAreas,
        locale: this.currentLocale,
        ungroupedLabel: this.$t('W: Uden prøveområde'),
        // Segment-included analyses render checked but locked, so this modal
        // can only add or remove customer-only ones.
        toOption: (analysis) => {
          const isInSegment = this.segmentIDSet.has(analysis.id)
          return {
            value: analysis.id,
            text: analysis.getName(this.currentLocale),
            description: isInSegment ? this.$t('W: I segment') : undefined,
            isDisabled: isInSegment,
          }
        },
      })
    },
  },

  methods: {
    ...mapActions(useModalStore, ['closeTop']),

    // Strip the segment IDs back out — they are only in modelValue to render
    // as checked, and must never end up in the customer-only selection.
    onCheckedUpdate(newCheckedIDs: string[]): void {
      this.localCustomerOnlyIDs = newCheckedIDs.filter(
        (id) => !this.segmentIDSet.has(id)
      )
    },

    onConfirmClick(): void {
      // Close before confirming: `onConfirm` may open a confirmation modal of its own (a removed
      // analysis that carries a deal), and closing afterwards would close THAT one instead — the
      // modal stack is last-in-first-out — leaving this picker open and Gem looking dead.
      this.closeTop()
      this.onConfirm(this.localCustomerOnlyIDs)
    },
  },
}
</script>
